/**
 * A refilling concurrency pool.
 *
 * This is what replaces the Flask app's server-side background scan: the browser
 * drives the per-app loop, so the work has to be bounded here instead. Unlike the
 * chunked Promise.allSettled pattern used on the server, this refills as each
 * task settles, so one slow app never stalls the others behind it.
 *
 * Results are delivered through onSettled as they arrive rather than returned in
 * a batch, so the UI can fill in row by row.
 */
export async function pooledForEach<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>
): Promise<void> {
  if (items.length === 0) return;

  let cursor = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));

  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const index = cursor++;
      if (index >= items.length) return;
      // task is responsible for its own error handling; a throw here would kill
      // this worker and silently reduce throughput for the rest of the run.
      await task(items[index]!);
    }
  });

  await Promise.all(workers);
}
